/**
 * Sandbox policy for the plugin iframe — the SINGLE source of truth shared by
 * every surface that mounts a plugin (the production renderer plus the builder
 * and dev preview panes). One constant guarantees a plugin behaves identically
 * in preview and in production; drift would let a capability appear on one
 * surface and not another.
 *
 * Tokens (order is cosmetic — the attribute is a set):
 *   - allow-scripts   — plugins are JS apps.
 *   - allow-forms     — plugins submit forms.
 *   - allow-downloads — plugins can trigger downloads (exports, generated files).
 *   - allow-popups + allow-popups-to-escape-sandbox — a plugin may open an
 *     external link in a new tab (e.g. `window.open(url, '_blank')` behind a
 *     button click). Without allow-popups the browser silently no-ops the call;
 *     allow-popups-to-escape-sandbox makes the opened tab a normal browsing
 *     context so the destination (YouTube, docs, …) isn't itself sandboxed and
 *     broken.
 *
 * SECURITY (accepted trade-off): with allow-popups a plugin can navigate the
 * user to ANY external URL with no host mediation and no confirmation — a
 * native `window.open` never reaches the host, so the platform cannot
 * gate/allowlist it at runtime. Outbound navigation is instead policed by the
 * plugin security-review pipeline (services/plugin-review-service), whose AI
 * analyzer is primed to scrutinize `window.open` targets.
 *
 * Deliberately ABSENT — never add either to a production surface:
 *   - allow-same-origin      → would let a plugin escape its opaque origin and
 *                              read/write cross-frame (defeats isolation).
 *   - allow-top-navigation*  → would let a plugin hijack the whole Fias tab.
 *
 * NO JS DIALOGS, and `allow-modals` would not change that. Because there is no
 * allow-same-origin the frame has an opaque origin, so it is cross-origin to
 * us, and browsers refuse dialogs from a cross-origin iframe whatever the
 * sandbox says. The call does not throw — `confirm()` returns false, `prompt()`
 * null, `alert()` nothing — so a guard written the obvious way becomes a button
 * that silently does nothing:
 *
 *     if (!window.confirm('Delete this?')) return;   // never proceeds
 *
 * Same shape as autoplay and clipboard-write below: a capability the opaque
 * origin denies silently. It is worse here only because the dev harness adds
 * allow-same-origin for its tooling, so the identical dialog WORKS locally —
 * Opportunity Graph shipped both Delete buttons dead this way (2026-09-06).
 * The SDK now pins the production behaviour in every environment and logs why
 * (packages/arche-sdk/src/sandbox-dialogs.ts), so the harness fails the same
 * way. A plugin that needs to ask the user must ask with its own UI.
 *
 * The dev harness (packages/plugin-dev-harness/.../harness.html) intentionally
 * runs a LOOSER sandbox (it adds allow-same-origin for local tooling). That is
 * a localhost-only convenience and must not be mirrored here.
 */
export const PLUGIN_IFRAME_SANDBOX =
  'allow-scripts allow-forms allow-downloads allow-popups allow-popups-to-escape-sandbox';

/**
 * Permissions Policy delegated to the plugin frame — the `allow` attribute.
 *
 * `autoplay` is here because without it a plugin cannot play audio AT ALL.
 * The feature defaults to `self`, and a sandbox without `allow-same-origin`
 * gives the frame an opaque origin, which is cross-origin to us — so the
 * feature is disabled inside it and `HTMLMediaElement.play()` rejects with
 * NotAllowedError. Crucially that is a POLICY denial, not the ordinary
 * autoplay heuristic: a user gesture inside the frame does not lift it, so
 * the usual "park the track and start it on first interaction" recovery just
 * fails forever. Flappyland's music was silent on staging for exactly this
 * reason while its sound effects (which fall back to a Web Audio synth) still
 * played — Tom, 2026-08-02.
 *
 * The trade-off accepted: a plugin can now start audio without waiting for a
 * gesture. That is an annoyance a user can leave, not a capability — it
 * grants no access to anything, and the browser's own autoplay heuristics
 * still apply on top. Sound is table stakes for the games the platform hosts.
 *
 * `clipboard-write` is here for the same class of reason: it defaults to
 * `self`, so `navigator.clipboard.write()` rejects in the opaque-origin frame
 * and a plugin cannot offer "copy this" AT ALL — the Image Editor's
 * copy-to-clipboard export was inert in the sandbox while its download and
 * share paths worked. Writing is a push the user initiated; it reads nothing,
 * and notably `clipboard-read` is NOT delegated — a frame that could read the
 * clipboard could harvest whatever the user last copied elsewhere, which is a
 * real capability rather than an annoyance.
 *
 * `fullscreen` is the third of the same shape, and the one a player asks for
 * by name. It defaults to `self`, so `Element.requestFullscreen()` inside the
 * opaque-origin frame rejects and `document.fullscreenEnabled` reads FALSE —
 * which means a plugin cannot even offer the button, since the honest way to
 * write that control is to hide it when the document says no. Flappyland has
 * shipped one since 2026-09-03 (ui/useFullscreen.tsx) and it had never once
 * appeared in production: on a phone in portrait the browser's address bar and
 * gesture strip are a real fraction of the play area, and nothing but this API
 * can take them back (Tom, 2026-09-08: "I thought we had made the capability
 * to make the game full screen on mobile, but I don't see that implemented").
 *
 * The trade-off accepted: a frame that fills the screen could dress itself up
 * as the platform, or as the OS. Three things bound it. The browser requires a
 * user GESTURE to enter and shows its own "press Esc to exit" / swipe-down
 * notice on the way in, so it is never silent and never unescapable. Escape and
 * the system gesture always work, and are not ours to suppress. And a plugin is
 * reviewed code, which is where a frame that abuses the whole display is
 * caught — the same line the popup trade-off above is drawn on. What it grants
 * is a SIZE, not an access: nothing about the frame's origin, storage, or
 * reach changes when it is full screen.
 *
 * Keep this MINIMAL for the same reason as the sandbox above: every feature
 * named here is one the plugin gets and cannot otherwise have. Nothing that
 * touches hardware or location (`camera`, `microphone`, `geolocation`,
 * `display-capture`) belongs here — those are capabilities, and capabilities
 * are Entities, not iframe attributes.
 */
export const PLUGIN_IFRAME_ALLOW = 'autoplay; clipboard-write; fullscreen';
